// Import the GrowthBook SDK
import { GrowthBook } from '@growthbook/growthbook';
import { v4 as uuidv4 } from 'uuid';
import { sendSegmentEvent } from './segment';
import { readMeta, readMetaBoolean } from './page';
import { accountId, accountType } from './jwt';
import { readDomainCookie, writeDomainCookie } from './domain_cookie';
import { performanceCookiesEnabled } from './oneTrust';

// define a global variable to store the GrowthBook instance
// but do not iniaialize it yet so that we can import this file without erroring out
let gb: GrowthBook | null;

// values from https://docs.growthbook.io/faq#how-do-i-make-my-own-identifier
const COOKIE_NAME = 'gbuuid';
const COOKIE_DAYS = 400; // 400 days is the max cookie duration for chrome

export const growthbookInstance = (): GrowthBook | null => gb;

const generateCookie = (): string => {
  const existingCookieId = readDomainCookie(COOKIE_NAME);
  if (existingCookieId) return existingCookieId;

  const newCookieId = uuidv4();
  writeDomainCookie(COOKIE_NAME, newCookieId, { expires: COOKIE_DAYS });
  return newCookieId;
};

const trackingCallback = (gbuuid: string) => {
  return (experiment, result) => {
    sendSegmentEvent('Experiment Viewed', {
      experimentId: experiment.key,
      variationId: result.key,
      gbuuid,
    });
  };
};

export const resetGrowthbook = () => {
  gb = null;
};

// this will return the growthbook instance if it has been initialized,
// otherwise it will initialize it and return the instance
export const setupGrowthbook = async (): Promise<GrowthBook | null> => {
  // if we have already initialized GrowthBook, return the existing instance
  if (gb) return gb;

  // client key needed to initialize GrowthBook
  const clientKey = readMeta('growthbook-client-key');
  // determines if we should enable the growthbook dev mode for experimentation and debugging
  const enableDevMode = readMetaBoolean('growthbook-dev-mode');

  // TODO: should we raise an error here instead?
  // As it currently stands, the app can check for null and handle it gracefully
  // if we raise an errror, that will need to be caught to handle it gracefully
  if (!clientKey) {
    console.error('GrowthBook client key missing');
    return null;
  }

  // Create an instance of GrowthBook
  // user id for targeting, this may be null
  const userId = accountId();
  const gbuuid = generateCookie();
  const role = accountType();

  try {
    gb = new GrowthBook({
      clientKey,
      enableDevMode,
      attributes: { userId, gbuuid, role },
      trackingCallback: trackingCallback(gbuuid),
    });

    await gb.init({ streaming: true });
  } catch (e) {
    console.error('Error initializing GrowthBook:', e);
    gb = null;
  }

  return gb;
};

export const isOn = (key: string): boolean => {
  if (gb) return gb.isOn(key);
  return false;
};

const createOrDestroyGrowthbook = async (): Promise<GrowthBook | null> => {
  // if functional cookies are enabled, we can use growthbook
  if (performanceCookiesEnabled()) {
    if (gb) {
      // growthbook has already been initialized
      return gb;
    }
    // growthbook doesn't exist, but it should.
    return setupGrowthbook();
  }

  // clear growthbook if functional cookies have been disabled.
  resetGrowthbook();
  return null;
};

// wrap the initialization of GrowthBook in a promise so that we can
// wait for the OneTrust library to load before initializing GrowthBook
// this function can be waited on if you rely on GrowthBook on startup
export const initGrowthbook = (): Promise<GrowthBook | null> => {
  // we want to listen for the first occurrence of the OneTrustPreferencesChanged event
  // because this is when we can be sure that the OneTrust library has loaded
  const firstInitializationPromise = new Promise<GrowthBook | null>((resolve) => {
    window.addEventListener('OneTrustPreferencesChanged', () => resolve(createOrDestroyGrowthbook()), { once: true });
  });

  // we want to re-initialize growthbook if the user changes their preferences
  window.addEventListener('OneTrustPreferencesChanged', createOrDestroyGrowthbook);

  return firstInitializationPromise;
};

export default {
  resetGrowthbook,
  initGrowthbook,
  isOn,
};
